import React from 'react';
import { Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { StructuredData } from '../components/seo/StructuredData';
import { BRAND } from '../config/brand';
import { LANGUAGE_OPTIONS, normalizeLanguage } from '../i18n/languages';
import { ramalUi } from '../i18n/ramal';

export default function RamalOverview() {
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const h = (key: Parameters<typeof ramalUi>[1]) => ramalUi(language, key);
  const languages = LANGUAGE_OPTIONS.map(item => item.label).join(' · ');
  const faqs = [
    { question: h('seoFaqBirth'), answer: h('intro') },
    { question: h('seoFaqPaid'), answer: `${h('premium')} ${h('seoPrivacy')}` },
    { question: h('seoFaqLanguages'), answer: languages },
  ];
  const url = `${BRAND.url}/ramal-prashna`;
  return <>
    <SEO title={`${h('seoName')} | ${BRAND.name}`} description={h('seoDescription')} path="/ramal-prashna" robots="index,follow" image="/logo512.png" keywords={`Ramala, Ramal Prashna, Ramal astrology, Indian geomancy, रमल प्रश्न, ${BRAND.name}`} />
    <StructuredData data={[
      { '@context': 'https://schema.org', '@type': 'WebPage', name: h('seoName'), description: h('seoDescription'), url, inLanguage: language, about: { '@type': 'Thing', name: 'Ramala (Ramal Prashna)' }, publisher: { '@type': 'Organization', name: BRAND.name, url: BRAND.url } },
      { '@context': 'https://schema.org', '@type': 'FAQPage', inLanguage: language, mainEntity: faqs.map(faq => ({ '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer } })) },
    ]} />
    <Container dir={language === 'ur' ? 'rtl' : 'ltr'} maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}><Stack spacing={4}>
      <Box component="header">
        <Typography component="h1" variant="h3" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{h('seoName')}</Typography>
        <Typography sx={{ mt: 2, lineHeight: 1.8 }} color="text.secondary">{h('seoIntro')}</Typography>
        <Typography sx={{ my: 2 }} fontWeight={700}>{h('seoFacts')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button component={RouterLink} to="/ramal" variant="contained" size="large">{h('open')}</Button>
          <Button component={RouterLink} to="/pricing" variant="outlined">{h('plans')}</Button>
        </Stack>
        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">{h('premium')}</Typography>
      </Box>
      <Box component="section"><Typography component="h2" variant="h5" fontWeight={700} sx={{ mb: 2 }}>{h('seoHow')}</Typography><Stack component="ol" spacing={2} sx={{ pl: 3, pr: language === 'ur' ? 3 : 0 }}>
        <Typography component="li">{h('seoStep1')}</Typography>
        <Typography component="li">{h('digital')} · {h('digitalNote')}</Typography>
        <Typography component="li">{h('manual')} · {h('manualNote')}</Typography>
        <Typography component="li">{h('chart')} · {h('rowNote')}</Typography>
      </Stack></Box>
      <Card variant="outlined"><CardContent><Stack spacing={1}>
        <Typography component="h2" variant="h5" fontWeight={700}>{h('history')}</Typography>
        <Typography>{h('seoPrivacy')}</Typography>
        <Typography variant="body2" color="text.secondary">{h('topic_adaptation')} {h('wish_adaptation')}</Typography>
      </Stack></CardContent></Card>
      <Stack component="section" spacing={3}>{faqs.map(faq => <Box key={faq.question}>
        <Typography component="h2" variant="h6" fontWeight={700}>{faq.question}</Typography>
        <Typography sx={{ mt: 1, lineHeight: 1.8 }} color="text.secondary">{faq.answer}</Typography>
      </Box>)}</Stack>
      <Typography variant="body2" color="text.secondary">{h('guidance')} {h('noTiming')}</Typography>
    </Stack></Container>
  </>;
}
